<script setup>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import _ from 'lodash';
  import moment from "moment";
  import { usePageLoadingStore } from "../../../../../stores/pageLoading";
  import adminJobfairStateService from "../../../services/jobfair/adminJobfairStateService";
  import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
  import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";

  const route = useRoute()
  const router = useRouter()
  const loadingStore = usePageLoadingStore()
  const { $pageListBoardNo, $toast } = getCurrentInstance().appContext.config.globalProperties;

  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const searchOpen = ref(true);
  const list = ref([]);
  const searchTypeRef = ref(null);

  // 검색조건 - 날짜검색기준 (all 모든 기간, term 기간 검색)
  const searchDurationTypeGroup = ref([
    { label:'모든 기간', value: 'all' },
    { label:'기간 검색', value: 'term' },
  ])

  const pageSizeGroup = ref([
    { label:"10개씩 보기", value: 10 },
    { label:"30개씩 보기", value: 30 },
    { label:"50개씩 보기", value: 50 },
    { label:"100개씩 보기", value: 100 },
  ])

  // text 검색. tab 마다 항목 다름.
  const searchTypeGroup = ref([
    { label:"이름", value: "userName", placeholder: "이름을 입력해주세요." },
    { label:'아이디', value: 'id', placeholder: "아이디를 입력해주세요." },
    { label:"회원코드", value: "code", placeholder: "회원코드를 입력해주세요."  },
  ]);
  
  const searchInputPlaceholder = computed(()=>{
    const option = _.find(searchTypeGroup.value, { value: searchInput.value.searchType });
    if(option) {
      return option.placeholder;
    } else {
      return '';
    }
  })

  // 검색 - searchType 셀렉트 박스 선택시
  const onSelectSearchTypeGroup = (option) => {
    searchInput.value.searchType = option.value;
  }

  const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
  }

  // 페이지당 개수 변경
  const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
  }

  const searchInput = ref({ // 학생/취업준비생(2) - 회원 정보(1)
    searchType: 'userName', // userName 이름, id 아이디, code 회원코드
    userInput: '',
    searchDurationType: 'all',
    startYmd: '',
    endYmd: '',
  });

  const retrieveList = () => {
    const now = new Date();
    const startYmd = searchInput.value.startYmd ? searchInput.value.startYmd : moment(now).format('YYYYMMDD');
    const endYmd = searchInput.value.endYmd ? searchInput.value.endYmd : moment(new Date(now.setDate(now.getDate() + 30))).format('YYYYMMDD');

    let paramObj = {
      searchType: searchInput.value.searchType,
      searchInput: searchInput.value.userInput,
      startYmd: searchInput.value.searchDurationType === 'all' ? '' : startYmd,
      endYmd: searchInput.value.searchDurationType === 'all' ? '' : endYmd,
      pageNumber: currentPage.value,
      size: pageSize.value,
    }
    loadingStore.loading = true;
    adminJobfairStateService.retrieveAllJobFairStuPaging(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        if (res.data.code.toLowerCase() === "success") {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      }).catch((res) => {
      loadingStore.loading = false;
      $toast("error", res);
    });
  };

  const onClickSearch = () => {
    currentPage.value = 1;
    retrieveList();
  }

  const clearSearchInput = () => {
    searchInput.value = {
      searchType: 'userName',
      userInput: '',
      searchDurationType: 'all',
      startYmd: '',
      endYmd: '',
    };
    searchTypeRef.value?.initSelectedOption('userName');
  }

  const openDetailInNewTab = (linkName, id = {}) => {
    const url = router.resolve({
      name: linkName,
      params: { id },
    }).href;

    window.open(url, '_blank');
  }

  onMounted(() => {
    retrieveList();
  })
</script>

<template>
  <div>
    <!-- s: 검색 -->
    <div class="filter-wrap" :class="{'open': searchOpen}">
      <p @click="searchOpen = !searchOpen">검색</p>
      <div class="filter">
        <dl>
          <dt>
            <KSASelect ref="searchTypeRef" :options="searchTypeGroup" class="select" @selectOption="onSelectSearchTypeGroup"/>
          </dt>
          <dd>
            <KSAInput class="text-box" v-model="searchInput.userInput" :placeholder="searchInputPlaceholder" />
          </dd>
        </dl>
        <dl>
          <dt>이력서 등록 및 <br/>약관 동의일</dt>
          <dd class="text-box">
            <div class="grid">
              <template v-for="group in searchDurationTypeGroup" key="item.value" >
                <KSARadioButton class="mr-md" :label="group.label" v-model="searchInput.searchDurationType" :value="group.value" />
              </template>
              <div style="display: inline-block;" v-if="searchInput.searchDurationType !== 'all'">
                <KSADatepickerTerms
                  :startDate="searchInput.startYmd"
                  :endDate="searchInput.endYmd"
                  @update:startDate="(value)=> searchInput.startYmd = value"
                  @update:endDate="(value)=> searchInput.endYmd = value"
                />
              </div>
            </div>
          </dd>
        </dl>
        <div class="btn-wrap">
          <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearchInput" />
          <KSAPrimaryButton text="검색" @click="onClickSearch" />
        </div>
      </div>
    </div>
    <!-- e: 검색 -->
    <!-- s: 버튼영역 -->
    <div class="list-top">
      <p>총 <span>{{ $number(totalCount)}}</span>개</p>
      <div>
        <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
      </div>
    </div>
    <!-- e: 버튼영역 -->
    <div class="table-form">
      <table class="horizontal">
        <tr>
          <th>NO</th>
          <th>회원코드</th>
          <th>이름</th>
          <th>아이디</th>
          <th>이력서 등록 및<br>
            정보 수집·활용 약관 동의일</th>
          <th class="th-red">이력서</th>
          <th class="th-red">입사지원</th>
          <th class="th-red">받은 면접제안</th>
        </tr>
        <tr v-for="user in list">
          <td>{{ user.boardNo }}</td>
          <td>{{ user.userCd }}</td>
          <td class="clickable blue">
            <span @click="openDetailInNewTab('MemberDetail', user.userId)">{{ user.userName }}</span>
          </td>
          <td>{{ user.userLoginId }}</td>
          <td>{{ $yyyymmddHHmmss(user.recruitAgreePrivacyDate) }}</td>
          <td>{{ user.resumeCnt }}</td>
          <td>{{ user.applicantCnt }}</td>
          <td>{{ user.interviewProposalEntryCnt }}</td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        상시채용관을 이용중인 회원정보가 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
</template>
<style scoped>
</style>
